import { useState } from 'react';
import { useBilling } from '../../context/BillingContext';
import './CurrentSubscription.css';

export function CurrentSubscription() {
  const { subscription, overview, cancel, reactivate } = useBilling();
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatPrice = (amount: number | undefined, currency: string | undefined) => {
    if (!amount) return 'N/A';
    return new Intl.NumberFormat('he-IL', {
      style: 'currency',
      currency: currency || 'ILS',
    }).format(amount);
  };

  const handleCancel = async () => {
    setIsProcessing(true);
    setError(null);
    try {
      await cancel(false, cancelReason);
      setShowCancelModal(false);
      setCancelReason('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to cancel subscription');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReactivate = async () => {
    setIsProcessing(true);
    setError(null);
    try {
      await reactivate();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to reactivate subscription');
    } finally {
      setIsProcessing(false);
    }
  };

  if (!subscription) {
    return (
      <div className="current-subscription no-sub">
        <div className="sub-icon">&#128274;</div>
        <h3>No Active Subscription</h3>
        <p>You're on the Free plan. Upgrade to unlock premium features!</p>
      </div>
    );
  }

  const isPendingCancellation = subscription.cancelAtPeriodEnd;

  return (
    <div className={`current-subscription ${isPendingCancellation ? 'cancelling' : ''}`}>
      {error && <div className="sub-error">{error}</div>}

      <div className="sub-header">
        <div className="sub-plan">
          <h3>{subscription.planName}</h3>
          <span className={`sub-status ${subscription.status.toLowerCase()}`}>
            {isPendingCancellation ? 'Cancelling' : subscription.status}
          </span>
        </div>
        <div className="sub-badge">{subscription.planCode.toUpperCase()}</div>
      </div>

      <div className="sub-details">
        <div className="sub-detail">
          <span className="detail-label">Current Period</span>
          <span className="detail-value">
            {formatDate(subscription.currentPeriodStart)} - {formatDate(subscription.currentPeriodEnd)}
          </span>
        </div>

        {isPendingCancellation ? (
          <div className="sub-detail warning">
            <span className="detail-label">Subscription Ends</span>
            <span className="detail-value">{formatDate(subscription.currentPeriodEnd)}</span>
          </div>
        ) : overview?.nextBillingDate ? (
          <div className="sub-detail">
            <span className="detail-label">Next Billing</span>
            <span className="detail-value">
              {formatPrice(overview.nextBillingAmount, overview.currency)} on {formatDate(overview.nextBillingDate)}
            </span>
          </div>
        ) : null}

        {subscription.defaultPaymentMethod && (
          <div className="sub-detail">
            <span className="detail-label">Payment Method</span>
            <span className="detail-value">
              {subscription.defaultPaymentMethod.brand.toUpperCase()} ending in {subscription.defaultPaymentMethod.last4}
            </span>
          </div>
        )}
      </div>

      <div className="sub-actions">
        {isPendingCancellation ? (
          <button
            className="btn-reactivate"
            onClick={handleReactivate}
            disabled={isProcessing}
          >
            {isProcessing ? 'Processing...' : 'Keep My Subscription'}
          </button>
        ) : (
          <button
            className="btn-cancel"
            onClick={() => setShowCancelModal(true)}
            disabled={isProcessing}
          >
            Cancel Subscription
          </button>
        )}
      </div>

      {showCancelModal && (
        <div className="cancel-modal-overlay">
          <div className="cancel-modal">
            <h3>Cancel Subscription?</h3>
            <p>
              Your subscription will remain active until{' '}
              <strong>{formatDate(subscription.currentPeriodEnd)}</strong>.
              After that, you'll be moved to the Free plan.
            </p>

            <div className="cancel-reason">
              <label>Reason for cancelling (optional)</label>
              <textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="Tell us why you're leaving..."
              />
            </div>

            <div className="cancel-actions">
              <button
                className="btn-keep"
                onClick={() => setShowCancelModal(false)}
                disabled={isProcessing}
              >
                Keep Subscription
              </button>
              <button
                className="btn-confirm-cancel"
                onClick={handleCancel}
                disabled={isProcessing}
              >
                {isProcessing ? 'Cancelling...' : 'Yes, Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
